import { useEffect, useState } from "react";
import { Copy, Check } from "lucide-react";
import { Alert, Button, Checkbox, DescriptionList, Dialog } from "../../ui";
import "./styles.scss";

type ShowApiKeyDialogProps = {
  isOpen: boolean;
  partnerName: string;
  apiKey: string;
  isRotation?: boolean;
  onClose: () => void;
};

const ShowApiKeyDialog = (props: ShowApiKeyDialogProps) => {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (props.isOpen) {
      setCopied(false);
      setAcknowledged(false);
    }
  }, [props.isOpen]);

  const handleClose = () => {
    if (!acknowledged) return;
    props.onClose();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(props.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — user can select manually.
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={handleClose}
      title={props.isRotation ? "New API Key" : "API Key Issued"}
      // It can only be dismissed once the admin confirms the key is saved
      hideClose={!acknowledged}
      busy={!acknowledged}
      closeOnBackdrop={false}
      footer={
        <Button disabled={!acknowledged} onClick={handleClose}>
          Done
        </Button>
      }
    >
      <div className="show-api-key">
        <Alert tone="warn">
          <strong>Copy this key now.</strong> It will not be shown again. Only the SHA-256 hash is stored on our servers
          — if lost, you must rotate it.
        </Alert>

        <DescriptionList columns={1} items={[{ label: "Partner", value: props.partnerName }]} />

        <div className="show-api-key__key">
          <code>{props.apiKey}</code>
          <Button variant="tint" size="sm" icon={copied ? <Check size={16} /> : <Copy size={16} />} onClick={handleCopy}>
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>

        <p className="show-api-key__usage">
          The partner sends this key on every request: <code>X-API-Key: {props.apiKey.slice(0, 16)}…</code>
        </p>

        <Checkbox
          checked={acknowledged}
          onChange={setAcknowledged}
          label="I have copied the key and stored it securely."
        />
      </div>
    </Dialog>
  );
};

export default ShowApiKeyDialog;
